import React from 'react';

import i18n from '@client/utils/i18n';
import { Area, AreaChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

interface CircadianTooltipData {
    time: string;
    spectrumMixName: string;
    lightRecipeName: string;
}

interface CircadianDataPoint {
    time: number;
    value: number;
    tooltip?: CircadianTooltipData;
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{ payload?: CircadianDataPoint }>;
}

interface CircadianChartProps {
    data: CircadianDataPoint[];
    referencePercent?: number;
    unit?: 'pfd' | 'irradiance';
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length && payload[0].payload && payload[0].payload.tooltip) {
        const tooltip = payload[0].payload.tooltip;
        return (
            <div className="custom-tooltip" style={{ background: 'white', padding: '10px', border: '1px solid #777' }}>
                <div>{tooltip.time}</div>
                <div>{tooltip.spectrumMixName}</div>
                <div>{tooltip.lightRecipeName}</div>
            </div>
        );
    }

    return null;
};

const CircadianChart = ({ data, referencePercent, unit = 'pfd' }: CircadianChartProps) => {
    if (!data.some((e) => e.time === 0)) {
        data.unshift({ time: 0, value: 0 });
    }
    if (!data.some((e) => e.time === 23 + 55 / 60)) {
        data.push({ time: 23 + 55 / 60, value: 0 });
    }
    let reference: number | null = null;

    if (referencePercent) {
        reference = (24 * referencePercent) / 100;
        if (reference === 0) {
            reference = 0.001;
        }
    }

    return (
        <ResponsiveContainer width="100%" aspect={3}>
            <AreaChart data={data} style={{ userSelect: 'none' }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis domain={[0, 24]} type="number" dataKey="time" scale="linear" ticks={[...Array(24).keys()].map((e) => e)} />
                <YAxis
                    label={{ value: `${i18n(unit) as string} (${i18n(unit + '_unit') as string})`, angle: -90, position: 'insideBottomLeft', offset: 20 }}
                    tickFormatter={(v: number) => parseFloat(v.toPrecision(2)).toString()}
                />
                <Legend />
                <Tooltip content={<CustomTooltip />} />
                {reference ? <ReferenceLine x={reference} stroke="red" label="" /> : null}

                <Area
                    name={i18n('hours') as string}
                    isAnimationActive={false}
                    animationDuration={0}
                    fillOpacity={0.1}
                    type="linear"
                    stroke="#777777"
                    dataKey="value"
                    dot={{ stroke: '#888', fill: '#888', r: 3 }}
                    activeDot={true}
                    strokeWidth={1}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default React.memo(CircadianChart);
